'use client';

import { Link, usePathname } from '@/i18n/routing';
import { ingdgLocaleSwitch } from '@/lib/locale-switch';
import { useLocale, useTranslations } from 'next-intl';
import { NastrojkiCookie } from '@/components/seo/nastrojki-cookie';
import {
  JAZYKI_DIKTOVKI_SSYLKI,
  JAZYKI_KONSPEKTA_INGDG,
  PODPIS_VSE_INSTRUMENTY,
  PUT_DIKTOVKI_INGDG,
  PUT_KATALOGA_INGDG,
  PUT_KONSPEKTA_INGDG,
  PUT_POKUPKI_KONSPEKTA,
} from '@/lib/ingdg-konspekt-ssylki';
import { Logo } from '@/components/ui/logo';
import { NashiProekty } from '@/components/layout/nashi-proekty';

export function Footer() {
  const t = useTranslations('footer');
  const pathname = usePathname();
  const locale = useLocale();
  const konspekt = JAZYKI_KONSPEKTA_INGDG.includes(locale);
  const year = String(new Date().getFullYear());

  const columns = [
    {
      title: t('product'),
      links: [
        { href: '/solutions', label: t('solutions') },
        { href: '/features', label: t('features') },
        { href: '/pricing', label: t('pricing') },
        { href: '/docs', label: t('docs') },
        { href: '/dashboard', label: t('dashboard') },
      ],
    },
    {
      title: t('tools'),
      links: [
        // Каталог всех инструментов своего языка (10.10.2026).
        { href: PUT_KATALOGA_INGDG, label: PODPIS_VSE_INSTRUMENTY[locale] ?? 'All tools' },
        // Конспект (04.10.2026) и его покупка на самом ingdg.com.
        ...(konspekt
          ? [
              { href: PUT_KONSPEKTA_INGDG, label: 'Konspekt' },
              { href: PUT_POKUPKI_KONSPEKTA, label: t('konspekt_kupit') },
            ]
          : []),
        ...(JAZYKI_DIKTOVKI_SSYLKI.includes(locale)
          ? [{ href: PUT_DIKTOVKI_INGDG, label: t('diktovka') }]
          : []),
        { href: '/tools/ai-readiness-checker', label: t('tools_ai') },
        { href: '/tools/llms-txt-generator', label: t('tools_llms') },
        { href: '/tools/seo-checker', label: t('tools_seo') },
      ],
    },
    {
      title: t('company'),
      links: [
        { href: '/about', label: t('about') },
        { href: '/faq', label: t('faq') },
        { href: '/blog', label: t('blog') },
        { href: '/contact', label: t('contact') },
      ],
    },
    {
      title: t('legal'),
      links: [
        { href: '/privacy', label: t('privacy') },
        { href: '/terms', label: t('terms') },
        // Согласие на рассылку (одобрено владельцем 06.10.2026): общий
        // текст для всех брендов, на ingdg.com с его адресом для связи.
        { href: '/legal/marketing-consent', label: t('marketing_consent') },
      ],
    },
  ];

  return (
    <footer className="border-t border-[var(--color-border)] bg-[var(--color-bg)]">
      <div className="max-w-[1280px] mx-auto px-6 py-12">
        {/*
          Расшифровка аббревиатуры: короткий бренд сам по себе ничего не говорит
          ни человеку, ни поисковику. Здесь он получает смысл и попадает
          в текст каждой страницы сайта.
        */}
        <div className="mb-10 max-w-md">
          <div className="flex items-center gap-2.5 mb-3">
            <Logo className="w-6 h-6 text-[var(--color-brand-500)]" />
            <span className="font-bold tracking-tight">INGDG</span>
            <span className="text-[var(--color-text-muted)]">— {t('brand_meaning')}</span>
          </div>
          <p className="text-sm text-[var(--color-text-muted)] leading-relaxed">
            {t('brand_meaning_desc')}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {columns.map((col) => (
            <div key={col.title}>
              <h4 className="font-semibold text-sm text-[var(--color-text)] mb-4">{col.title}</h4>
              <ul className="space-y-2">
                {col.links.map((l) => (
                  <li key={l.href}>
                    <Link
                      href={l.href}
                      className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
                {/* Снова открыть плашку согласия (16.10.2026). */}
                {col.title === t('legal') && (
                  <NastrojkiCookie
                    kakLi
                    className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
                  />
                )}
              </ul>
            </div>
          ))}

          {/* Locale */}
          <div>
            <h4 className="font-semibold text-sm text-[var(--color-text)] mb-4">{t('language')}</h4>
            <div className="flex flex-wrap gap-2">
              {(['en', 'es', 'sr', 'ru'] as const).map((locale) => {
                // Со статьи, не переведённой на этот язык, ведём в список,
                // с русского Конспекта — на aisearch.ru: тот же путь дал бы
                // 404 и человеку, и поисковику (lib/locale-switch.ts).
                const cel = ingdgLocaleSwitch(pathname, locale);
                return 'adres' in cel ? (
                  <a
                    key={locale}
                    href={cel.adres}
                    hrefLang={locale}
                    className="px-2 py-1 rounded text-xs text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] border border-[var(--color-border)] hover:border-[var(--color-accent-500)] transition-colors uppercase"
                  >
                    {locale}
                  </a>
                ) : (
                  <Link
                    key={locale}
                    href={cel.put}
                    locale={locale}
                    hrefLang={locale}
                    className="px-2 py-1 rounded text-xs text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] border border-[var(--color-border)] hover:border-[var(--color-accent-500)] transition-colors uppercase"
                  >
                    {locale}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {/* Соседние продукты (перелинковка, 19.10.2026). */}
        <NashiProekty sajt="ingdg" locale={locale} />

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-[var(--color-border)] flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-[var(--color-text-muted)]">{t('copyright', { year })}</p>
          <p className="text-xs text-[var(--color-text-muted)]">{t('tagline')}</p>
        </div>
      </div>
    </footer>
  );
}
